import { cn } from '@/lib/utils';

/**
 * What the app shows while it is loading: its own mark, not a generic spinner.
 *
 * The mark sits still and a brand-tinted ring sweeps around it, so the thing
 * carrying the identity is the thing that is legible — a rotating logo is
 * unreadable, which defeats the point of using one. The `.animate-pulse-ring`
 * halo and the sweep are both animations, so `prefers-reduced-motion` takes
 * them out globally and what is left is the mark on its own, which still reads
 * as "waiting" because it is the only thing on the screen.
 *
 * `BrandLogo` is deliberately not reused: it resolves a sub-brand out of the
 * auth store, and this renders before that store has bootstrapped.
 */
export function BrandLoader({
  label = 'Loading',
  className,
}: {
  /** Named for screen readers; the mark alone says nothing to them. */
  label?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center', className)} role="status">
      <span className="relative grid h-16 w-16 place-items-center">
        <span
          aria-hidden
          className="absolute inset-0 animate-pulse-ring rounded-2xl bg-brand-500/20"
        />
        <svg aria-hidden viewBox="0 0 64 64" className="absolute inset-0 h-16 w-16 animate-spin-slow">
          <circle
            cx="32"
            cy="32"
            r="30"
            fill="none"
            stroke="rgb(var(--brand-500))"
            strokeOpacity="0.18"
            strokeWidth="2"
          />
          <circle
            cx="32"
            cy="32"
            r="30"
            fill="none"
            stroke="rgb(var(--brand-500))"
            strokeWidth="2"
            strokeLinecap="round"
            // A quarter of the circumference, so the sweep reads as one arc.
            strokeDasharray="47 141"
          />
        </svg>
        {/* The `-dark` file is the white-tinted lockup drawn for dark grounds,
            and `-light` the dark one — the same swap BrandLogo makes. */}
        <img
          src="/brand/mark-dark.png"
          alt=""
          width="36"
          height="36"
          className="relative h-9 w-9 object-contain dark:hidden"
        />
        <img
          src="/brand/mark-light.png"
          alt=""
          width="36"
          height="36"
          className="relative hidden h-9 w-9 object-contain dark:block"
        />
      </span>
      <span className="sr-only">{label}</span>
    </div>
  );
}
